import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Avatar } from '@klaro7/ui';

import { useAuth } from '@/features/auth/hooks/useAuth';
import { ProfessionalQuickControls } from '@/features/professional-profile/components/ProfessionalQuickControls';
import { useMyOnlineStatus } from '@/features/professional-profile/hooks/useMyOnlineStatus';
import { professionalStatusDisplay } from '@/features/professionals/lib/statusDisplay';
import { resolveUploadUrl } from '@/lib/uploads';

const MENU_ID = 'dashboard-user-menu';

export function UserMenu() {
  const { user, logout } = useAuth();
  const onlineStatus = useMyOnlineStatus();
  const navigate = useNavigate();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    const handlePointerDown = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    const handleFocusIn = (event: FocusEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      setIsOpen(false);
      triggerRef.current?.focus();
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('focusin', handleFocusIn);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('focusin', handleFocusIn);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!isOpen) return;
    panelRef.current?.focus();
  }, [isOpen]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  if (!user) return null;

  const fullName = `${user.profile?.firstName || ''} ${user.profile?.lastName || ''}`.trim();
  const avatarUrl = resolveUploadUrl(user.profile?.avatar);
  const isProfessional = user.role === 'professional';
  const statusDisplay = onlineStatus ? professionalStatusDisplay(onlineStatus) : null;

  return (
    <div className="relative" ref={menuRef}>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setIsOpen((open) => !open)}
        className="flex items-center gap-2 rounded-lg p-1.5 transition-colors hover:bg-neutral-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-teal focus-visible:ring-offset-2"
        aria-label={isOpen ? 'Chiudi menu account' : 'Apri menu account'}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        aria-controls={MENU_ID}
      >
        <span className="relative inline-flex">
          <Avatar src={avatarUrl} fallback={fullName || user.email} size="sm" />
          {statusDisplay && (
            <span className="absolute -bottom-0.5 -right-0.5 flex h-3 w-3" aria-hidden="true">
              {statusDisplay.pulse && (
                <span className={`absolute inline-flex h-full w-full motion-safe:animate-ping rounded-full opacity-75 ${statusDisplay.dotClass}`} />
              )}
              <span
                className={`relative inline-flex h-3 w-3 rounded-full border-2 border-white ${statusDisplay.dotClass}`}
              />
            </span>
          )}
        </span>
      </button>

      {isOpen && (
        <div
          ref={panelRef}
          id={MENU_ID}
          role="dialog"
          aria-label="Menu account"
          tabIndex={-1}
          className="absolute right-0 z-50 mt-2 max-h-[calc(100dvh-5rem)] w-[min(20rem,calc(100vw-2rem))] overflow-y-auto rounded-lg border border-neutral-200 bg-white py-1 shadow-lg outline-none motion-safe:animate-fade-in"
        >
          <div className="border-b border-neutral-100 px-4 py-3">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-neutral-900">{fullName || 'Utente'}</p>
                <p className="truncate text-xs text-neutral-600">{user.email}</p>
              </div>
              {statusDisplay && (
                <span className={`mt-0.5 inline-flex shrink-0 items-center gap-1.5 rounded-full px-2 py-1 text-xs font-medium ${statusDisplay.badgeClass}`}>
                  <span className={`h-1.5 w-1.5 rounded-full ${statusDisplay.dotClass}`} aria-hidden="true" />
                  {statusDisplay.label}
                </span>
              )}
            </div>
          </div>

          {isProfessional && <ProfessionalQuickControls />}

          <div className="py-1">
            <button
              type="button"
              onClick={() => {
                setIsOpen(false);
                navigate('/dashboard/profile');
              }}
              className="flex min-h-11 w-full items-center gap-2 px-4 py-2 text-sm text-neutral-700 hover:bg-brand-teal-50 focus:outline-none focus-visible:bg-brand-teal-50"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              Profilo
            </button>

            <button
              type="button"
              onClick={() => {
                setIsOpen(false);
                navigate('/dashboard/settings');
              }}
              className="flex min-h-11 w-full items-center gap-2 px-4 py-2 text-sm text-neutral-700 hover:bg-brand-teal-50 focus:outline-none focus-visible:bg-brand-teal-50"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              Impostazioni
            </button>
          </div>

          <div className="border-t border-neutral-100 py-1">
            <button
              type="button"
              onClick={handleLogout}
              className="flex min-h-11 w-full items-center gap-2 px-4 py-2 text-sm text-error hover:bg-brand-teal-50 focus:outline-none focus-visible:bg-brand-teal-50"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              Esci
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
